import React, { useState } from 'react';
import { SENTENCE_PRACTICE } from '../data/sentencePractice';
import { isTrial } from '../data/storage';
import StructureDiagram from './StructureDiagram';

const TRIAL_COUNT = 2;

const TOPIC_FILTERS = ['All', 'Education', 'Environment', 'Health', 'Technology', 'Society'];

function bandTagStyle(target) {
  return target.includes('7') && !target.includes('6')
    ? { bg: '#fef3c7', color: '#b45309', border: '#fde68a' }
    : { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' };
}

// ── Auxiliary verb variations (Not only…but also) ─────────────────────
const AUX_COLORS = [
  { bg: '#e8f0fb', border: '#c0d4f0', label: '#1a5fa8', badge: '#1a2a4a' },
  { bg: '#f0faf4', border: '#bbf7d0', label: '#14532d', badge: '#15803d' },
  { bg: '#fef3c7', border: '#fde68a', label: '#92400e', badge: '#b45309' },
  { bg: '#f5f3ff', border: '#ddd6fe', label: '#5b21b6', badge: '#7c3aed' },
  { bg: '#fff5f5', border: '#fecaca', label: '#991b1b', badge: '#dc2626' },
  { bg: '#fff8f0', border: '#fde8cc', label: '#9a3412', badge: '#c2410c' },
  { bg: '#f0f9ff', border: '#bae6fd', label: '#075985', badge: '#0369a1' },
];

function AuxVariationsSection({ variations, title = 'Variations — click any card to expand' }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {variations.map((v, i) => {
          const c   = AUX_COLORS[i % AUX_COLORS.length];
          const open = openIdx === i;
          return (
            <div key={i}
              style={{ border: `1px solid ${open ? c.border : '#e4e8f0'}`, borderRadius: 9, background: open ? c.bg : '#fafafa', overflow: 'hidden', transition: 'all .2s' }}>
              {/* Header row — always visible */}
              <button onClick={() => setOpenIdx(open ? null : i)}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
                <span style={{ background: c.badge, color: '#fff', borderRadius: 6, padding: '3px 10px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 700, flexShrink: 0, letterSpacing: '.04em' }}>
                  {v.aux}
                </span>
                <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, fontWeight: 600, color: '#1a2a4a', flex: 1 }}>
                  {v.label}
                </span>
                <span style={{ color: '#9aa3b8', fontSize: 14, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
              </button>

              {/* Expanded content */}
              {open && (
                <div style={{ padding: '0 14px 14px' }}>
                  {/* Rule */}
                  <div style={{ background: c.badge, borderRadius: 6, padding: '7px 12px', marginBottom: 10, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#ffffff', lineHeight: 1.6 }}>
                    {v.rule}
                  </div>
                  {/* When to use */}
                  <div style={{ fontSize: 12.5, color: '#5a6a88', lineHeight: 1.65, marginBottom: 12, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    <strong style={{ color: '#1a2a4a' }}>When to use: </strong>{v.when}
                  </div>
                  {/* Examples */}
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: c.label, marginBottom: 8 }}>Examples</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {v.examples.map((ex, ei) => (
                      <div key={ei} style={{ background: '#ffffff', border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.badge}`, borderRadius: '0 7px 7px 0', padding: '10px 13px' }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8.5, color: '#9aa3b8', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 5 }}>
                          Topic: {ex.topic}
                        </div>
                        <div style={{ fontSize: 13.5, color: '#14532d', lineHeight: 1.75, fontFamily: "'Times New Roman',serif" }}>
                          {highlightAux(ex.sentence, v.aux)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function highlightAux(sentence, auxRaw) {
  const auxWords = auxRaw.split(/\s*[/|]\s*/).map(w => w.trim()).filter(Boolean);
  const pattern = new RegExp(`\\b(${auxWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'i');
  const match = sentence.match(pattern);
  if (!match) return sentence;
  const idx = sentence.indexOf(match[0]);
  return (
    <span>
      {sentence.slice(0, idx)}
      <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 3, padding: '0 3px', fontWeight: 700 }}>{match[0]}</span>
      {sentence.slice(idx + match[0].length)}
    </span>
  );
}

// ── Single example card (weak → strong) ─────────────────────────────
function ExampleCard({ example }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {example.topic && (
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: '#9aa3b8', marginBottom: 7, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>Topic: {example.topic}</span>
          {example.category && (
            <span style={{ background: '#e8f0fb', color: '#1a5fa8', border: '1px solid #c0d4f0', borderRadius: 100, padding: '1px 8px', fontSize: 8.5 }}>{example.category}</span>
          )}
        </div>
      )}
      <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderLeft: '4px solid #dc2626', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: 4 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#dc2626', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>✗ Weak</div>
        <div style={{ fontSize: 13.5, color: '#7f1d1d', lineHeight: 1.7, fontFamily: "'Times New Roman',serif", fontStyle: 'italic' }}>{example.weak}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '2px 0' }}>
        <span style={{ fontSize: 17, color: '#16a34a' }}>↓</span>
      </div>
      <div style={{ background: '#f0faf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #16a34a', borderRadius: '0 8px 8px 0', padding: '10px 14px' }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#16a34a', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>✓ Strong (Band 7+)</div>
        <div style={{ fontSize: 13.5, color: '#14532d', lineHeight: 1.7, fontFamily: "'Times New Roman',serif" }}>{example.strong}</div>
      </div>
    </div>
  );
}

// ── All examples stacked (for showAllExamples structures) ─────────────
function ExampleList({ examples, topicFilter }) {
  const filtered = topicFilter === 'All'
    ? examples
    : examples.filter(e => e.category === topicFilter);
  if (!filtered.length) return (
    <div style={{ background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 9, padding: '20px', textAlign: 'center', color: '#9aa3b8', fontSize: 13 }}>
      No examples in this category — select <strong>All</strong> to see all.
    </div>
  );
  return (
    <div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 12 }}>
        Examples — Weak vs. Strong ({filtered.length} shown)
      </div>
      {filtered.map((ex, i) => <ExampleCard key={i} example={ex} />)}
    </div>
  );
}

// ── Example carousel (one at a time, with prev/next) ─────────────────
function ExampleCarousel({ examples, topicFilter }) {
  const filtered = topicFilter === 'All'
    ? examples
    : examples.filter(e => e.category === topicFilter);
  const [idx, setIdx] = useState(0);

  const safeIdx = Math.min(idx, Math.max(0, filtered.length - 1));
  const example = filtered[safeIdx];

  if (!filtered.length) {
    return (
      <div style={{ background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 9, padding: '24px', textAlign: 'center', color: '#9aa3b8', fontSize: 13 }}>
        No examples in this category — select <strong>All</strong> to see all examples.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88' }}>
          Examples — Weak vs. Strong
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#9aa3b8' }}>
            {safeIdx + 1} / {filtered.length}
          </span>
          <button onClick={() => setIdx(Math.max(0, safeIdx - 1))} disabled={safeIdx === 0}
            style={{ border: '1px solid #dde3ed', background: safeIdx === 0 ? '#f7f9fc' : '#ffffff', color: safeIdx === 0 ? '#d1d5db' : '#1a2a4a', borderRadius: 5, width: 28, height: 28, cursor: safeIdx === 0 ? 'default' : 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ‹
          </button>
          <button onClick={() => setIdx(Math.min(filtered.length - 1, safeIdx + 1))} disabled={safeIdx === filtered.length - 1}
            style={{ border: '1px solid #dde3ed', background: safeIdx === filtered.length - 1 ? '#f7f9fc' : '#ffffff', color: safeIdx === filtered.length - 1 ? '#d1d5db' : '#1a2a4a', borderRadius: 5, width: 28, height: 28, cursor: safeIdx === filtered.length - 1 ? 'default' : 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            ›
          </button>
        </div>
      </div>
      {example && (
        <div style={{ marginBottom: 4 }}>
          {example.topic && (
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: '#9aa3b8', marginBottom: 7, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>Topic: {example.topic}</span>
              {example.category && (
                <span style={{ background: '#e8f0fb', color: '#1a5fa8', border: '1px solid #c0d4f0', borderRadius: 100, padding: '1px 8px', fontSize: 8.5 }}>{example.category}</span>
              )}
            </div>
          )}
          <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderLeft: '4px solid #dc2626', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: 5 }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#dc2626', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>✗ Weak</div>
            <div style={{ fontSize: 13.5, color: '#7f1d1d', lineHeight: 1.7, fontFamily: "'Times New Roman',serif", fontStyle: 'italic' }}>{example.weak}</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '3px 0' }}>
            <span style={{ fontSize: 18, color: '#16a34a' }}>↓</span>
          </div>
          <div style={{ background: '#f0faf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #16a34a', borderRadius: '0 8px 8px 0', padding: '10px 14px' }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#16a34a', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>✓ Strong (Band 7+)</div>
            <div style={{ fontSize: 13.5, color: '#14532d', lineHeight: 1.7, fontFamily: "'Times New Roman',serif" }}>{example.strong}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Synonym groups ────────────────────────────────────────────────────
function SynonymGroups({ groups, copiedWord, onCopy }) {
  return (
    <div>
      {groups.map((group, i) => (
        <div key={i} style={{ marginBottom: 14, background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 9, padding: '12px 15px' }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, color: '#1a2a4a', marginBottom: 3 }}>{group.category}</div>
          <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 9 }}>{group.context}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {group.words.map((word, wi) => {
              const isIt = copiedWord === word;
              return (
                <button key={wi} onClick={() => onCopy(word)}
                  style={{ background: isIt ? '#ede9fe' : '#f5f3ff', color: isIt ? '#5b21b6' : '#7c3aed', border: `1px solid ${isIt ? '#c4b5fd' : '#ddd6fe'}`, borderRadius: 100, padding: '3px 11px', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all .12s' }}>
                  {isIt ? '✓ Copied!' : word}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Exercise context ──────────────────────────────────────────────────
function ExerciseContext({ exercise }) {
  const card = (label, text, bg = '#e8f0fb', border = '#c0d4f0', color = '#1e3a8a') => (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8 }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13.5, color, lineHeight: 1.65, fontFamily: "'Times New Roman',serif" }}>{text}</div>
    </div>
  );
  if (exercise.point1) return <div>{card('Point 1', exercise.point1)}{card('Point 2 (stronger)', exercise.point2, '#f0fdf4', '#86efac', '#15803d')}</div>;
  if (exercise.sentence1) return <div>{card('Sentence 1', exercise.sentence1)}{card('Sentence 2', exercise.sentence2)}</div>;
  if (exercise.given) return <div>{card('Given Text', exercise.given, '#fafafa', '#e8e8e8', '#374151')}{exercise.task && card('Your Task', exercise.task, '#fff8f0', '#fde8cc', '#9a3412')}</div>;
  if (exercise.concession) return <div>{card('Concession — acknowledge this point', exercise.concession, '#fff5f5', '#fecaca', '#991b1b')}{card('Your Main Argument — stronger counter-point', exercise.mainPoint, '#f0faf4', '#bbf7d0', '#15803d')}</div>;
  if (exercise.original) return <div>{card('Original Question to Paraphrase', exercise.original, '#eff6ff', '#bfdbfe', '#1e3a8a')}</div>;
  if (exercise.sentence) return (
    <div>
      {card('Original Sentence (contains weak words)', exercise.sentence, '#fff5f5', '#fecaca', '#7f1d1d')}
      {exercise.weakWords && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#dc2626', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 5 }}>Weak words/phrases to replace:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {exercise.weakWords.map((w, i) => (
              <span key={i} style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 100, padding: '2px 10px', fontSize: 12 }}>{w}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  if (exercise.weak) return <div>{card('Weak version to replace', exercise.weak, '#fff5f5', '#fecaca', '#7f1d1d')}{exercise.context && card('Context', exercise.context, '#fafafa', '#e8e8e8', '#374151')}</div>;
  if (exercise.context) return card('Context', exercise.context, '#fafafa', '#e8e8e8', '#374151');
  return null;
}

// ── Tip chips ─────────────────────────────────────────────────────────
function TipChips({ tips }) {
  return (
    <div style={{ marginTop: 11, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {tips.map((tip, i) => (
        <span key={i} style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          💡 {tip}
        </span>
      ))}
    </div>
  );
}

// ── Topic filter chips ────────────────────────────────────────────────
function TopicFilter({ active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
      {TOPIC_FILTERS.map(f => (
        <button key={f} onClick={() => onChange(f)}
          style={{ border: `1px solid ${active === f ? '#1a2a4a' : '#dde3ed'}`, background: active === f ? '#1a2a4a' : '#ffffff', color: active === f ? '#ffffff' : '#5a6a88', borderRadius: 100, padding: '4px 13px', fontSize: 12, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: active === f ? 600 : 400, transition: 'all .12s' }}>
          {f}
        </button>
      ))}
    </div>
  );
}

// ── Structure panel ───────────────────────────────────────────────────
function StructurePanel({ structure, candidate, topicFilter, onComplete }) {
  const [view,        setView]        = useState('learn');
  const [showDiagram, setShowDiagram] = useState(false);
  const [exIdx,       setExIdx]       = useState(0);
  const [writing,     setWriting]     = useState('');
  const [showAnswer,  setShowAnswer]  = useState(false);
  const [aiFeedback,  setAiFeedback]  = useState(null);
  const [loadingFb,   setLoadingFb]   = useState(false);
  const [fbError,     setFbError]     = useState('');
  const [done,        setDone]        = useState(false);
  const [copied,      setCopied]      = useState('');

  const filteredExercises = topicFilter === 'All'
    ? (structure.exercises || [])
    : (structure.exercises || []).filter(e => e.category === topicFilter);

  const exercises = filteredExercises.length ? filteredExercises : (structure.exercises || []);
  const exercise  = exercises[exIdx];

  function copyWord(word) {
    try { navigator.clipboard.writeText(word).then(() => { setCopied(word); setTimeout(() => setCopied(''), 2000); }); }
    catch { setCopied(word); setTimeout(() => setCopied(''), 2000); }
  }

  function changeExercise(delta) {
    const next = exIdx + delta;
    if (next >= 0 && next < exercises.length) {
      setExIdx(next); setWriting(''); setShowAnswer(false); setAiFeedback(null); setFbError('');
    } else if (delta > 0) {
      setDone(true);
      onComplete && onComplete(structure.id);
    }
  }

  async function handleAIFeedback() {
    if (!writing.trim()) { setFbError('Please write your sentence first.'); return; }
    setFbError(''); setLoadingFb(true); setAiFeedback(null);
    try {
      const res = await fetch('/api/anthropic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 500,
          system: 'You are an expert IELTS Writing Task 2 examiner. The student is practising a specific grammatical structure. Give feedback in 3 parts: (1) What they did well — quote their exact words. (2) What needs improving — be specific about the structure. (3) One improved version of their exact sentence. Keep feedback to 150 words maximum. Be encouraging but precise. Return ONLY valid JSON.',
          messages: [{ role: 'user', content: `Structure: "${structure.name}"\nFormula: "${structure.formula}"\n\nExercise prompt: ${exercise.prompt}\n\nStudent's sentence:\n"${writing}"\n\nModel answer for reference:\n"${exercise.modelAnswer}"\n\nReturn ONLY valid JSON:\n{\n  "wellDone": "Specific praise quoting their exact words",\n  "improvement": "Specific structural issue to fix",\n  "rewrite": "One improved version of their sentence"\n}` }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '{}';
      setAiFeedback(JSON.parse(text.replace(/```json|```/g, '').trim()));
    } catch (e) { setFbError('Could not get feedback: ' + e.message); }
    setLoadingFb(false);
  }

  const btc = bandTagStyle(structure.bandTarget);

  return (
    <div>
      {/* Band + explanation */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ background: btc.bg, color: btc.color, border: `1px solid ${btc.border}`, borderRadius: 100, padding: '3px 12px', fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
          {structure.bandTarget}
        </span>
        <div style={{ fontSize: 13, color: '#5a6a88', lineHeight: 1.6, flex: 1 }}>{structure.explanation}</div>
      </div>

      {/* Formula */}
      <div style={{ background: '#1a2a4a', borderRadius: 9, padding: '13px 18px', marginBottom: 12 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#b8860b', letterSpacing: '.14em', textTransform: 'uppercase', marginBottom: 6 }}>Formula</div>
        <div style={{ fontFamily: "'Courier New',monospace", fontSize: 13.5, color: '#ffffff', lineHeight: 1.6 }}>{structure.formula}</div>
      </div>

      {/* Diagram toggle */}
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setShowDiagram(v => !v)}
          style={{ border: '1px solid #dde3ed', background: showDiagram ? '#e8f0fb' : '#f7f9fc', color: showDiagram ? '#1a5fa8' : '#5a6a88', borderRadius: 7, padding: '6px 14px', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 500, transition: 'all .15s' }}>
          {showDiagram ? '📐 Hide Diagram' : '📐 See Diagram'}
        </button>
        {showDiagram && <div style={{ marginTop: 10 }}><StructureDiagram structureId={structure.id} /></div>}
      </div>

      {/* Learn / Practice toggle */}
      <div style={{ display: 'flex', background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 8, padding: 3, width: 'fit-content', marginBottom: 20 }}>
        {[['learn', '📖 Learn'], ['practice', '✏️ Practice']].map(([v, label]) => (
          <button key={v} onClick={() => setView(v)}
            style={{ border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all .15s', background: view === v ? '#ffffff' : 'transparent', color: view === v ? '#1a2a4a' : '#5a6a88', boxShadow: view === v ? '0 1px 4px rgba(0,0,0,0.10)' : 'none' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── LEARN VIEW ── */}
      {view === 'learn' && (
        <div>
          {/* Variations section (all structures) */}
          {structure.variations && (
            <AuxVariationsSection variations={structure.variations} title={structure.variationsTitle} />
          )}

          {/* Synonym groups (Paraphrasing) */}
          {structure.synonymGroups && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 10 }}>
                Synonym Bank — click any word to copy
              </div>
              <SynonymGroups groups={structure.synonymGroups} copiedWord={copied} onCopy={copyWord} />
            </div>
          )}

          {/* Examples — stacked (showAllExamples) or carousel */}
          {structure.examples && structure.examples.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {structure.showAllExamples
                ? <ExampleList examples={structure.examples} topicFilter={topicFilter} />
                : <ExampleCarousel examples={structure.examples} topicFilter={topicFilter} />
              }
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <button onClick={() => setView('practice')}
              style={{ background: '#1a2a4a', color: '#ffffff', border: 'none', padding: '11px 28px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 2px 8px rgba(26,42,74,0.2)' }}>
              Start Practising →
            </button>
          </div>
        </div>
      )}

      {/* ── PRACTICE VIEW ── */}
      {view === 'practice' && (
        <div>
          {done ? (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>🎉</div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: '#1a2a4a', marginBottom: 8 }}>All exercises completed!</div>
              <div style={{ fontSize: 13.5, color: '#5a6a88', marginBottom: 24, lineHeight: 1.65 }}>
                You have practised all {exercises.length} exercise{exercises.length !== 1 ? 's' : ''} for <strong>{structure.name}</strong>.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => { setDone(false); setExIdx(0); setWriting(''); setShowAnswer(false); setAiFeedback(null); }}
                  style={{ background: '#f7f9fc', border: '1px solid #dde3ed', color: '#1a2a4a', padding: '10px 22px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Start Again
                </button>
                <button onClick={() => setView('learn')}
                  style={{ background: '#1a2a4a', color: '#ffffff', border: 'none', padding: '10px 22px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Back to Learn
                </button>
              </div>
            </div>
          ) : exercise ? (
            <div>
              {/* Filter note if active */}
              {topicFilter !== 'All' && filteredExercises.length === 0 && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff8f0', border: '1px solid #fde8cc', borderRadius: 7, fontSize: 12.5, color: '#92400e' }}>
                  No exercises in <strong>{topicFilter}</strong> — showing all exercises.
                </div>
              )}
              {/* Progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#5a6a88', flexShrink: 0 }}>
                  {exIdx + 1} / {exercises.length}
                </div>
                <div style={{ flex: 1, background: '#f0f4f8', borderRadius: 100, height: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${((exIdx + 1) / exercises.length) * 100}%`, height: '100%', background: '#b8860b', borderRadius: 100, transition: 'width .3s' }} />
                </div>
                {(exercise.topic || exercise.title) && (
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, background: '#e8f0fb', color: '#1a5fa8', border: '1px solid #c0d4f0', borderRadius: 100, padding: '2px 9px', flexShrink: 0 }}>
                    {exercise.title || exercise.topic}
                  </span>
                )}
              </div>

              <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2a4a', marginBottom: 12, lineHeight: 1.5 }}>{exercise.prompt}</div>
              <div style={{ marginBottom: 14 }}><ExerciseContext exercise={exercise} /></div>

              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 6 }}>Your Answer</div>
              <textarea
                value={writing}
                onChange={e => setWriting(e.target.value)}
                placeholder={`Write your sentence here using the ${structure.name} structure…`}
                rows={4}
                style={{ width: '100%', background: '#ffffff', border: '1.5px solid #c0c0c0', borderRadius: 8, padding: '12px 14px', color: '#1a1a1a', fontSize: 14, lineHeight: 1.85, fontFamily: "'Times New Roman',serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box', transition: 'border-color .2s' }}
              />

              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button onClick={() => { setShowAnswer(!showAnswer); setAiFeedback(null); }}
                  style={{ background: showAnswer ? '#f0faf4' : '#f7f9fc', border: `1px solid ${showAnswer ? '#bbf7d0' : '#dde3ed'}`, color: showAnswer ? '#15803d' : '#5a6a88', padding: '9px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 500 }}>
                  {showAnswer ? '🙈 Hide Answer' : '👁 Show Model Answer'}
                </button>
                <button onClick={handleAIFeedback} disabled={loadingFb || !writing.trim()}
                  style={{ background: (!writing.trim() || loadingFb) ? '#f0f4f8' : '#1a2a4a', border: 'none', color: (!writing.trim() || loadingFb) ? '#9aa3b8' : '#ffffff', padding: '9px 16px', borderRadius: 7, cursor: (!writing.trim() || loadingFb) ? 'default' : 'pointer', fontSize: 12.5, fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 500, opacity: (!writing.trim() || loadingFb) ? 0.8 : 1, transition: 'all .15s' }}>
                  {loadingFb ? '⏳ Analysing…' : '🤖 Get AI Feedback'}
                </button>
              </div>

              {fbError && <div style={{ marginTop: 8, padding: '8px 13px', background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: 7, fontSize: 12.5, color: '#dc2626' }}>{fbError}</div>}

              {showAnswer && (
                <div style={{ marginTop: 14, background: '#f0faf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #16a34a', borderRadius: '0 9px 9px 0', padding: '13px 16px' }}>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#15803d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>✓ Model Answer</div>
                  <div style={{ fontSize: 14, color: '#14532d', lineHeight: 1.85, fontFamily: "'Times New Roman',serif", marginBottom: exercise.tips?.length ? 10 : 0 }}>
                    {exercise.modelAnswer}
                  </div>
                  {exercise.tips?.length > 0 && <TipChips tips={exercise.tips} />}
                </div>
              )}

              {aiFeedback && (
                <div style={{ marginTop: 14, background: '#ffffff', border: '1px solid #c8d0e0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(26,42,74,0.08)' }}>
                  <div style={{ background: '#1a2a4a', padding: '10px 16px', fontSize: 13, fontWeight: 700, color: '#ffffff', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>🤖 AI Examiner Feedback</div>
                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {aiFeedback.wellDone && (
                      <div style={{ background: '#f0faf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '10px 13px' }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#15803d', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>✓ What you did well</div>
                        <div style={{ fontSize: 13, color: '#14532d', lineHeight: 1.65 }}>{aiFeedback.wellDone}</div>
                      </div>
                    )}
                    {aiFeedback.improvement && (
                      <div style={{ background: '#fff8f0', border: '1px solid #fde8cc', borderRadius: 7, padding: '10px 13px' }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#c2410c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>→ What to improve</div>
                        <div style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.65 }}>{aiFeedback.improvement}</div>
                      </div>
                    )}
                    {aiFeedback.rewrite && (
                      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '10px 13px' }}>
                        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#1d4ed8', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>✎ Improved version</div>
                        <div style={{ fontSize: 13.5, color: '#1e3a8a', lineHeight: 1.75, fontFamily: "'Times New Roman',serif", fontStyle: 'italic' }}>{aiFeedback.rewrite}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18, gap: 8 }}>
                <button onClick={() => changeExercise(-1)} disabled={exIdx === 0}
                  style={{ background: '#f7f9fc', border: '1px solid #dde3ed', color: exIdx === 0 ? '#d1d5db' : '#1a2a4a', padding: '9px 18px', borderRadius: 7, cursor: exIdx === 0 ? 'default' : 'pointer', fontSize: 12.5, fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: exIdx === 0 ? 0.5 : 1 }}>
                  ← Previous
                </button>
                <button onClick={() => changeExercise(1)}
                  style={{ background: '#b8860b', border: 'none', color: '#ffffff', padding: '9px 22px', borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 2px 8px rgba(184,134,11,0.3)' }}>
                  {exIdx < exercises.length - 1 ? 'Next Exercise →' : 'Complete ✓'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px', color: '#9aa3b8' }}>No exercises available.</div>
          )}
        </div>
      )}

      {copied && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1a2a4a', color: '#ffffff', borderRadius: 8, padding: '10px 16px', fontSize: 12.5, fontFamily: "'Plus Jakarta Sans',sans-serif", boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 500 }}>
          ✓ "{copied}" copied to clipboard
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────
export default function SentencePractice({ candidate }) {
  const [activeIdx,          setActiveIdx]          = useState(0);
  const [topicFilter,        setTopicFilter]        = useState('All');
  const [completedStructures, setCompletedStructures] = useState(new Set());

  const trial = isTrial(candidate);
  const all   = SENTENCE_PRACTICE.structures;
  const total = all.length;

  function handleTab(idx) {
    if (trial && idx >= TRIAL_COUNT) return;
    setActiveIdx(idx);
  }

  function handleComplete(structureId) {
    setCompletedStructures(prev => new Set([...prev, structureId]));
  }

  const isLocked = trial && activeIdx >= TRIAL_COUNT;
  const completedCount = completedStructures.size;

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: '#1a2a4a', marginBottom: 5 }}>
          Sentence Practice
        </div>
        <div style={{ fontSize: 13, color: '#5a6a88', lineHeight: 1.65 }}>
          Master 7 essential grammatical structures used in Band 7+ essays.
          {trial && (
            <span style={{ marginLeft: 8, background: '#fff8f0', border: '1px solid #fde8cc', color: '#b8620a', borderRadius: 100, padding: '1px 10px', fontSize: 11.5, fontWeight: 600 }}>
              Free trial: first 2 structures
            </span>
          )}
        </div>
      </div>

      {/* Progress tracker */}
      <div style={{ background: '#f7f9fc', border: '1px solid #dde3ed', borderRadius: 9, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: '#5a6a88', flexShrink: 0 }}>
          Completed: {completedCount}/{total}
        </div>
        <div style={{ flex: 1, background: '#e8ecf2', borderRadius: 100, height: 6, overflow: 'hidden' }}>
          <div style={{ width: `${(completedCount / total) * 100}%`, height: '100%', background: '#b8860b', borderRadius: 100, transition: 'width .4s' }} />
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {all.map((s, i) => (
            <div key={s.id} title={s.name}
              style={{ width: 10, height: 10, borderRadius: '50%', background: completedStructures.has(s.id) ? '#b8860b' : trial && i >= TRIAL_COUNT ? '#e8ecf2' : '#dde3ed', border: `1.5px solid ${completedStructures.has(s.id) ? '#92400e' : '#c8d0e0'}`, transition: 'background .3s' }} />
          ))}
        </div>
      </div>

      {/* Topic filter */}
      <TopicFilter active={topicFilter} onChange={setTopicFilter} />

      {/* Structure tabs */}
      <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #dde3ed', marginBottom: 22, gap: 0 }}>
        {all.map((s, idx) => {
          const locked   = trial && idx >= TRIAL_COUNT;
          const isActive = idx === activeIdx;
          const btc      = bandTagStyle(s.bandTarget);
          const done     = completedStructures.has(s.id);
          return (
            <button key={s.id} onClick={() => handleTab(idx)}
              style={{ border: 'none', borderBottom: `2px solid ${isActive && !locked ? '#b8860b' : 'transparent'}`, background: 'none', padding: '9px 14px', color: locked ? '#d1d5db' : isActive ? '#b8860b' : '#5a6a88', cursor: locked ? 'default' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              {locked ? <span style={{ fontSize: 11 }}>🔒</span> : done ? <span style={{ fontSize: 11, color: '#b8860b' }}>✓</span> : null}
              {s.name}
              <span style={{ background: btc.bg, color: btc.color, border: `1px solid ${btc.border}`, borderRadius: 100, padding: '1px 7px', fontSize: 9, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>
                {s.bandTarget}
              </span>
            </button>
          );
        })}
      </div>

      {isLocked ? (
        <div style={{ background: '#fff8f0', border: '1px solid #fde8cc', borderRadius: 10, padding: '36px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, color: '#1a2a4a', marginBottom: 8 }}>
            {all[activeIdx]?.name} — Standard / Premium
          </div>
          <div style={{ fontSize: 13, color: '#5a6a88', lineHeight: 1.65 }}>
            This structure requires a Standard or Premium subscription.<br />
            Contact Mr. Ahmed Salem on WhatsApp to upgrade your access.
          </div>
        </div>
      ) : (
        <StructurePanel
          key={`${activeIdx}-${topicFilter}`}
          structure={all[activeIdx]}
          candidate={candidate}
          topicFilter={topicFilter}
          onComplete={handleComplete}
        />
      )}
    </div>
  );
}
